/**
 * Income fields by category. The rep picks the category in Personal details and
 * the income tab asks only what that category actually needs — a salaried case
 * has no turnover, an agriculturist has no salary slip.
 */
export const INCOME_CATEGORIES = [
  'Salaried',
  'Self-Employed Professional',
  'Self-Employed Non-Professional',
  'Business',
  'Pensioner',
  'Agriculturist',
];

const GST = [
  { key: 'gst_registered', label: 'GST registered', type: 'select', options: ['Yes', 'No'] },
  { key: 'gst_number', label: 'GST number', type: 'text', when: { gst_registered: 'Yes' } },
  { key: 'gst_certificate', label: 'GST certificate', type: 'doc', docType: 'gst_certificate', when: { gst_registered: 'Yes' } },
];

const ITR = [
  { key: 'itr_years_available', label: 'ITR years available', type: 'select', options: ['None', '1 year', '2 years', '3+ years'] },
  { key: 'itr_1', label: 'ITR — latest year', type: 'doc', docType: 'itr_1', unless: { itr_years_available: ['', 'None'] } },
  { key: 'itr_2', label: 'ITR — previous year', type: 'doc', docType: 'itr_2', unless: { itr_years_available: ['', 'None', '1 year'] } },
];

const SELF_EMPLOYED_BUSINESS = [
  { key: 'firm_name', label: 'Firm / trade name', type: 'text' },
  { key: 'constitution', label: 'Constitution', type: 'select', options: ['Proprietorship', 'Partnership', 'LLP', 'Private Limited', 'Other'] },
  { key: 'nature_of_business', label: 'Nature of business', type: 'text' },
  { key: 'business_vintage_years', label: 'Business vintage (years)', type: 'number' },
  { key: 'annual_turnover', label: 'Annual turnover (₹)', type: 'number' },
  { key: 'profit_after_tax', label: 'Profit after tax (₹)', type: 'number' },
  { key: 'business_registration', label: 'Business registration (Udyam / Shop Act)', type: 'doc', docType: 'business_registration' },
  ...ITR,
  ...GST,
];

export const CATEGORY_FIELDS = {
  Salaried: [
    { key: 'employer_name', label: 'Employer name', type: 'text' },
    { key: 'designation', label: 'Designation', type: 'text' },
    { key: 'total_work_experience', label: 'Total work experience (years)', type: 'number' },
    { key: 'current_job_experience', label: 'Experience in current job (years)', type: 'number' },
    { key: 'salary_mode', label: 'Salary mode', type: 'select', options: ['Bank transfer', 'Cheque', 'Cash'] },
    { key: 'net_monthly_salary', label: 'Net monthly salary (₹)', type: 'number' },
    { key: 'salary_slip_1', label: 'Salary slip — month 1', type: 'doc', docType: 'salary_slip_1' },
    { key: 'salary_slip_2', label: 'Salary slip — month 2', type: 'doc', docType: 'salary_slip_2' },
    { key: 'salary_slip_3', label: 'Salary slip — month 3', type: 'doc', docType: 'salary_slip_3' },
  ],
  'Self-Employed Professional': [
    { key: 'profession', label: 'Profession', type: 'select', options: ['Doctor', 'Chartered Accountant', 'Architect', 'Lawyer', 'Engineer', 'Consultant', 'Other'] },
    { key: 'qualification', label: 'Qualification', type: 'text' },
    { key: 'practice_vintage_years', label: 'Years in practice', type: 'number' },
    { key: 'annual_receipts', label: 'Annual professional receipts (₹)', type: 'number' },
    ...ITR,
    ...GST,
  ],
  'Self-Employed Non-Professional': SELF_EMPLOYED_BUSINESS,
  Business: SELF_EMPLOYED_BUSINESS,
  Pensioner: [
    { key: 'pension_source', label: 'Pension source', type: 'select', options: ['Central Government', 'State Government', 'Defence', 'PSU', 'EPS / EPFO', 'Private', 'Other'] },
    { key: 'monthly_pension', label: 'Monthly pension (₹)', type: 'number' },
    { key: 'pension_account_bank', label: 'Pension credited to', type: 'bank' },
    { key: 'pension_order', label: 'Pension payment order', type: 'doc', docType: 'pension_order' },
  ],
  Agriculturist: [
    { key: 'land_holding_acres', label: 'Land holding (acres)', type: 'number' },
    { key: 'land_ownership', label: 'Land ownership', type: 'select', options: ['Owned', 'Leased', 'Partly owned'] },
    { key: 'crop_type', label: 'Main crop / produce', type: 'text' },
    { key: 'irrigation_type', label: 'Irrigation', type: 'select', options: ['Rain-fed', 'Borewell', 'Canal', 'Drip', 'Other'] },
    { key: 'annual_agri_income', label: 'Annual income from agriculture (₹)', type: 'number' },
    { key: 'land_record', label: 'Land record (RTC / Pahani / 7-12)', type: 'doc', docType: 'land_record' },
  ],
};

export default CATEGORY_FIELDS;
